import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import Detalle from "@/pages/Detalle";
import { AdminAuthProvider } from "@/lib/adminAuth";
import { PortalAuthProvider } from "@/lib/portalAuth";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PortalProtectedRoute } from "@/components/portal/ProtectedRoute";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { OfflineBanner } from "@/components/common/OfflineBanner";
import { InstallPrompt } from "@/components/common/InstallPrompt";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import Login from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";
import UnidadesList from "@/pages/admin/UnidadesList";
import UnidadForm from "@/pages/admin/UnidadForm";
import ImportarUnidades from "@/pages/admin/ImportarUnidades";
import ImportarFotos from "@/pages/admin/ImportarFotos";
import UnidadDetalleAdmin from "@/pages/admin/UnidadDetalleAdmin";
import Embarques from "@/pages/admin/Embarques";
import EmbarqueDetalle from "@/pages/admin/EmbarqueDetalle";
import Clientes from "@/pages/admin/Clientes";
import Usuarios from "@/pages/admin/Usuarios";
import Notificaciones from "@/pages/admin/Notificaciones";
import Plantillas from "@/pages/admin/Plantillas";
import Etapas from "@/pages/admin/Etapas";
import Tipos from "@/pages/admin/Tipos";
import Cuenta from "@/pages/admin/Cuenta";
import PortalLogin from "@/pages/portal/Login";
import PortalUnidades from "@/pages/portal/Unidades";
import PortalUnidadDetalle from "@/pages/portal/UnidadDetalle";
import PortalCuenta from "@/pages/portal/Cuenta";
import RecuperarContrasena from "@/pages/RecuperarContrasena";
import RestablecerContrasena from "@/pages/RestablecerContrasena";

// El panel interno (/admin) y el portal corporativo (/portal) muestran VINs, nombres
// de clientes corporativos y teléfonos de terceros: nunca deben grabarse en PostHog.
function PrivacyGuard() {
  const location = useLocation();
  useEffect(() => {
    const ph = window.posthog;
    if (!ph || typeof ph.stopSessionRecording !== "function") return;
    const rutaSensible = location.pathname.startsWith("/admin") || location.pathname.startsWith("/portal");
    if (rutaSensible) {
      ph.stopSessionRecording();
    } else {
      ph.startSessionRecording?.();
    }
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
    <div className="App">
      <BrowserRouter>
        <AdminAuthProvider>
          <PortalAuthProvider>
            <Toaster position="top-right" />
            <OfflineBanner />
            <InstallPrompt />
            <PrivacyGuard />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/unidad/:vin" element={<Detalle />} />
              <Route path="/recuperar-contrasena" element={<RecuperarContrasena />} />
              <Route path="/restablecer-contrasena" element={<RestablecerContrasena />} />

              <Route path="/admin/login" element={<Login />} />
              <Route element={<ProtectedRoute roles={["admin", "operador"]} />}>
                <Route element={<AdminLayout />}>
                  <Route path="/admin" element={<Dashboard />} />
                  <Route path="/admin/unidades" element={<UnidadesList />} />
                  <Route path="/admin/unidades/nueva" element={<UnidadForm />} />
                  <Route path="/admin/unidades/importar" element={<ImportarUnidades />} />
                  <Route path="/admin/unidades/fotos" element={<ImportarFotos />} />
                  <Route path="/admin/unidades/:id" element={<UnidadDetalleAdmin />} />
                  <Route path="/admin/unidades/:id/editar" element={<UnidadForm />} />
                  <Route path="/admin/embarques" element={<Embarques />} />
                  <Route path="/admin/embarques/:booking" element={<EmbarqueDetalle />} />
                  <Route path="/admin/notificaciones" element={<Notificaciones />} />
                  <Route path="/admin/plantillas" element={<Plantillas />} />
                  <Route path="/admin/cuenta" element={<Cuenta />} />
                  <Route element={<ProtectedRoute roles={["admin"]} />}>
                    <Route path="/admin/clientes" element={<Clientes />} />
                    <Route path="/admin/usuarios" element={<Usuarios />} />
                    <Route path="/admin/etapas" element={<Etapas />} />
                    <Route path="/admin/tipos" element={<Tipos />} />
                  </Route>
                </Route>
              </Route>

              <Route path="/portal/login" element={<PortalLogin />} />
              <Route element={<PortalProtectedRoute />}>
                <Route element={<PortalLayout />}>
                  <Route path="/portal/unidades" element={<PortalUnidades />} />
                  <Route path="/portal/unidad/:vin" element={<PortalUnidadDetalle />} />
                  <Route path="/portal/cuenta" element={<PortalCuenta />} />
                </Route>
              </Route>
            </Routes>
          </PortalAuthProvider>
        </AdminAuthProvider>
      </BrowserRouter>
    </div>
    </ErrorBoundary>
  );
}

export default App;
